<template>
  <div class="relative inline-flex">
    <button
      type="button"
      :class="chipClass"
      :title="tooltipText"
      @click="open = !open"
    >
      <span aria-hidden="true">{{ health.emoji }}</span>
      <span>{{ health.label }}</span>
    </button>

    <div
      v-if="open"
      class="absolute right-0 top-full z-30 mt-2 w-72 border border-slate-200 bg-white p-3 text-sm shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/30"
    >
      <dl class="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <template v-if="tooltipRows.length">
          <template
            v-for="row in tooltipRows"
            :key="row.key"
          >
            <dt>{{ row.label }}</dt>
            <dd class="text-right font-semibold text-slate-900 dark:text-slate-100">{{ row.value }}</dd>
          </template>
        </template>
        <template v-else>
          <dt>Status</dt>
          <dd class="text-right font-semibold text-slate-900 dark:text-slate-100">{{ health.label }}</dd>
        </template>
      </dl>

      <button
        type="button"
        class="mt-3 inline-flex h-9 w-full items-center justify-center bg-slate-950 px-3 text-sm font-semibold text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-600 dark:hover:bg-indigo-500"
        :disabled="busy"
        @click="copyBadgeMarkdown"
      >
        {{ badge ? 'Copy markdown' : 'Get badge' }}
      </button>

      <p
        v-if="message"
        class="mt-2 text-xs"
        :class="messageToneClass"
        role="status"
      >
        {{ message }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useApi } from '../../composables/useApi'

const props = defineProps({
  health: {
    type: Object,
    required: true,
  },
  workspaceSlug: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['badge-created'])
const api = useApi()
const open = ref(false)
const busy = ref(false)
const message = ref('')
const messageTone = ref('ok')
const badge = ref(props.health.badge || null)

watch(() => props.health.badge, (nextBadge) => {
  badge.value = nextBadge || null
})

const counts = computed(() => props.health.counts || {})
const tooltipRows = computed(() => {
  return Object.entries(counts.value)
    .filter(([key, value]) => key !== 'days_since_ship' || value != null)
    .map(([key, value]) => ({
      key,
      label: COUNT_LABELS[key] || humanizeKey(key),
      value: formatCountValue(key, value),
    }))
})
const tooltipText = computed(() => {
  return tooltipRows.value.length
    ? tooltipRows.value.map((row) => `${row.label}: ${row.value}`).join(' | ')
    : props.health.note || props.health.label
})
const chipClass = computed(() => {
  const stateClass = {
    shipping: 'border-green-200 bg-green-50 text-green-800 dark:border-green-900/60 dark:bg-green-950/40 dark:text-green-300',
    wandering: 'border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-900/60 dark:bg-yellow-950/40 dark:text-yellow-300',
    fixated: 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-300',
    deep_shit: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300',
    growing: 'border-green-200 bg-green-50 text-green-800 dark:border-green-900/60 dark:bg-green-950/40 dark:text-green-300',
    ones_burning: 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-300',
    cooling: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300',
    graveyard: 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200',
  }[props.health.state] || 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'

  return [
    'inline-flex h-8 items-center gap-1.5 border px-2.5 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-950',
    stateClass,
  ]
})
const messageToneClass = computed(() => {
  return messageTone.value === 'error'
    ? 'text-red-700 dark:text-red-300'
    : 'text-slate-500 dark:text-slate-400'
})

const COUNT_LABELS = {
  open_in_play: 'Open in play',
  opened_14d: 'Opened 14d',
  closed_14d: 'Closed 14d',
  oldest_wip_days: 'Oldest WIP',
  days_since_close: 'Since close',
  ideas: 'Ideas',
  hot_score: 'Hot score',
  touches_30d: 'Touches 30d',
  corpses: 'Corpses',
  newest_touch_days: 'Newest touch',
}

function humanizeKey(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function formatCountValue(key, value) {
  const displayValue = value ?? 0
  return key.endsWith('_days') || key.startsWith('days_since_')
    ? `${displayValue}d`
    : displayValue
}

async function copyBadgeMarkdown() {
  if (busy.value) return

  busy.value = true
  message.value = ''
  messageTone.value = 'ok'

  try {
    if (!badge.value) {
      badge.value = await api.createBadgeToken(props.workspaceSlug)
      emit('badge-created', badge.value)
    }

    await writeClipboard(badge.value.markdown)
    message.value = 'Copied markdown.'
  } catch (error) {
    messageTone.value = 'error'
    message.value = error?.message || 'Could not copy markdown.'
  } finally {
    busy.value = false
  }
}

async function writeClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.top = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}
</script>
